import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, ShieldCheck } from 'lucide-react';
import PageHero from '@/components/shared/PageHero';
import SectionHeader from '@/components/shared/SectionHeader';
import { publicApi } from '@/lib/api';
import useSEO from '@/hooks/useSEO';

export default function CSREligibilityDocumentsPage() {
    const [downloads, setDownloads] = useState([]);

    useSEO({
        title: 'Our CSR Eligibility Documents',
        description:
            'Downloadable statutory registrations and certificates that qualify VVVSI Trust as an eligible CSR partner — 80G, 12A, CSR-1, annual reports and compliance documents.',
    });

    useEffect(() => {
        publicApi.getDownloads().then(setDownloads).catch(() => {});
    }, []);

    return (
        <div data-testid="csr-eligibility-documents-page">
            <PageHero
                eyebrow="CSR Projects · Compliance"
                title="Our CSR Eligibility Documents"
                subtitle="Statutory registrations and certificates that qualify VVVSI Trust as an eligible CSR partner. All documents are downloadable."
            />

            <section className="section-pad">
                <div className="container-page">
                    <SectionHeader
                        eyebrow="Compliance"
                        title="All compliance & registration documents"
                        description="Every document required to route your CSR contribution directly to VVVSI Trust."
                    />
                    {downloads.length > 0 ? (
                        <div
                            className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                            data-testid="csr-eligibility-documents"
                        >
                            {downloads.map((d) => (
                                <Card
                                    key={d.id}
                                    className="h-full bg-card border-border card-lift"
                                    data-testid={`eligibility-doc-${d.id}`}
                                >
                                    <CardContent className="p-5">
                                        <div className="flex items-start gap-3">
                                            <div className="h-11 w-11 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center shrink-0">
                                                <ShieldCheck className="h-5 w-5" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                {d.category && (
                                                    <div className="inline-flex items-center text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-1">
                                                        {d.category}
                                                    </div>
                                                )}
                                                <div className="font-semibold text-sm leading-snug mb-1">
                                                    {d.title}
                                                </div>
                                                {d.description && (
                                                    <div className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                                                        {d.description}
                                                    </div>
                                                )}
                                                <div className="mt-3 flex items-center justify-between gap-2">
                                                    {d.file_size && (
                                                        <span className="text-[10px] text-muted-foreground">
                                                            {d.file_size}
                                                        </span>
                                                    )}
                                                    <a
                                                        href={d.file_url}
                                                        target="_blank"
                                                        rel="noreferrer noopener"
                                                        data-testid={`eligibility-download-${d.id}`}
                                                    >
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5"
                                                        >
                                                            <Download className="h-3.5 w-3.5 mr-1.5" />
                                                            Download
                                                        </Button>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <div className="mt-8 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                            Eligibility documents will appear here shortly.
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
